import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { toaster } from '@/components/Toaster'
import { LocationFilters, useLocationsList } from '@/feature/locations/api/useLocationsList'
import { SearchResult } from '@/feature/map/hooks/useAutoCompleteLocationSearch'

import { GET_ALL_LIMIT } from '@/lib/core/constants/constants'
import { usePopover } from '@/lib/core/hooks/usePopover'

import { TaskLocationPayload, useUpdateLocations } from '../../api'
import { TaskDefinition } from '../../types/task'
import { TaskStatus } from '../CreateTaskModal'
import { LocationSearchRef } from '../LocationSearch/LocationSearch'
import {
  defaultTaskDefinitionValues,
  TaskLocation,
  TaskLocationPopoutProps,
} from './TaskLocationPopout.types'
import {
  buildEditPayloadForExisting,
  buildEditPayloadForGeoPoint,
  buildEditPayloadForPlace,
  buildPlaceGeoJson,
  formatSearchResultToTaskLocation,
  getPlacesServiceDetails,
  isLineString,
} from './TaskLocationPopout.utils'

export const useTaskLocationPopout = ({
  task,
  locationInput,
  onUpdated,
  mode,
}: Pick<TaskLocationPopoutProps, 'task' | 'locationInput' | 'onUpdated' | 'mode'>) => {
  const taskDefinition = (mode === 'CREATE' ? defaultTaskDefinitionValues : task) as TaskDefinition

  const buttonRef = useRef<HTMLButtonElement>(null)
  const dialogRef = useRef<HTMLDialogElement>(null)
  const searchInputRef = useRef<LocationSearchRef>(null)
  const focusTimeoutRef = useRef<number | undefined>(undefined)
  const { positionPopover } = usePopover({
    buttonRef,
    dialogRef,
    defaultPopoverDimensions: { width: 324, height: 275 },
  })
  const { trigger: updateLocations } = useUpdateLocations(taskDefinition.taskDefinition.id)

  const [filters, setFilters] = useState<LocationFilters>({ pageSize: GET_ALL_LIMIT })
  const { data } = useLocationsList(filters)
  const [, setSearch] = useState('')
  const [geoLocations, setGeoLocations] = useState<SearchResult[]>([])
  const [location, setLocation] = useState<TaskLocation>()

  useEffect(() => {
    setLocation(locationInput)
  }, [locationInput])

  const handleSearch = useCallback((term?: string) => {
    setSearch(term || '')
    setFilters((prev) => ({ ...prev, search: term || '' }))
  }, [])

  const handleSearchUpdated = useCallback((term: string) => handleSearch(term), [handleSearch])
  const handleSearchedResults = useCallback(
    (locations: SearchResult[]) => setGeoLocations(locations),
    [],
  )

  const handleClearSearch = useCallback(() => setSearch(''), [])

  const showPopover = (e: React.MouseEvent) => {
    e.stopPropagation()
    positionPopover()
    dialogRef.current?.showPopover()

    // The popover needs a frame before the input can take focus. Tracked so it can be
    // cancelled: left uncancelled, it fires after unmount and touches a detached input.
    window.clearTimeout(focusTimeoutRef.current)
    focusTimeoutRef.current = window.setTimeout(() => {
      searchInputRef.current?.focus()
    }, 100)
  }

  useEffect(() => () => window.clearTimeout(focusTimeoutRef.current), [])

  const handleClose = useCallback(() => {
    handleClearSearch()
    searchInputRef.current?.clear()
  }, [])

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    const handleToggle = (event: Event) => {
      const toggleEvent = event as ToggleEvent
      if (toggleEvent.newState === 'closed') {
        handleClose()
      }
    }

    dialog.addEventListener('toggle', handleToggle)
    return () => {
      dialog.removeEventListener('toggle', handleToggle)
    }
  }, [handleClose])

  const resolveTemporaryGeoJson = async (selectedLocation: TaskLocation) => {
    const service = new google.maps.places.PlacesService(document.createElement('div'))
    const place = await getPlacesServiceDetails(service, selectedLocation.id as string)
    const geometryLocation = place?.geometry?.location?.toJSON()
    selectedLocation.geoJson = buildPlaceGeoJson(geometryLocation!)
    return { place, geometryLocation: geometryLocation! }
  }

  const handleCreateLocation = async (selectedLocation: TaskLocation) => {
    if (selectedLocation.type === 'temporary' && !selectedLocation.geoJson) {
      await resolveTemporaryGeoJson(selectedLocation)
    }

    setLocation(selectedLocation)
    onUpdated(selectedLocation)
    dialogRef.current?.hidePopover()
  }

  const handleEditLocation = async (selectedLocation: TaskLocation) => {
    const correspondingLocationIndex = taskDefinition.taskDefinition.locations.findIndex(
      (loc) => loc.id === location?.id,
    )
    const payload: TaskLocationPayload[] = taskDefinition.taskDefinition.locations.map((loc) => ({
      originalObjectId: loc.originalObjectId ?? undefined,
      type: loc.type,
      name: loc.name,
      address: loc.address,
      geoJson: loc.geoJson,
    }))

    if (selectedLocation.type === 'temporary' && selectedLocation.geoJson) {
      payload[correspondingLocationIndex] = buildEditPayloadForGeoPoint(selectedLocation)
    } else if (selectedLocation.type === 'temporary') {
      const { place, geometryLocation } = await resolveTemporaryGeoJson(selectedLocation)
      payload[correspondingLocationIndex] = buildEditPayloadForPlace(
        selectedLocation,
        place,
        geometryLocation,
      )
    } else {
      payload[correspondingLocationIndex] = buildEditPayloadForExisting(selectedLocation)
    }

    await updateLocations({ locations: payload })
    toaster({ type: 'success', message: 'Task locations updated' })
    onUpdated(null)
    dialogRef.current?.hidePopover()
  }

  const handleUpdateLocations = async (selectedLocation: TaskLocation) => {
    try {
      if (mode === 'CREATE') {
        await handleCreateLocation(selectedLocation)
      } else {
        await handleEditLocation(selectedLocation)
      }
    } catch {
      toaster({
        type: 'error',
        message: 'Failed to update task locations. Please try again.',
      })
    }
  }

  const searchedLocations = useMemo(() => {
    const displayLocations: TaskLocation[] = []
    if (data.results.length) {
      displayLocations.push(...data.results)
    }
    if (geoLocations.length) {
      displayLocations.push(...geoLocations.map(formatSearchResultToTaskLocation))
    }

    displayLocations.sort((a, b) => {
      const aIsLineString = isLineString(a) ? 1 : 0
      const bIsLineString = isLineString(b) ? 1 : 0
      return aIsLineString - bIsLineString
    })

    return displayLocations
  }, [data.results, geoLocations])

  const isDisabled = useMemo(() => {
    return [TaskStatus.COMPLETED, TaskStatus.IN_PROGRESS].includes(
      taskDefinition.status as TaskStatus,
    )
  }, [taskDefinition])

  return {
    taskDefinition,
    location,
    searchedLocations,
    isDisabled,
    buttonRef,
    dialogRef,
    searchInputRef,
    showPopover,
    handleSearchUpdated,
    handleSearchedResults,
    handleUpdateLocations,
  } as const
}

export type UseTaskLocationPopoutReturn = ReturnType<typeof useTaskLocationPopout>
